import { NodeHelpersType, NodeId } from '@candulabs/craft';
import flatten from 'lodash/flatten';

// import from craft/constants/craft to break a circular dependency
import { CRAFT_ROOT_ID } from 'craft/constants/craft';
import { BuiltInComponents, selectionBlacklist, tooltipRootTypes } from 'craft/constants/schema';
import { useEditor } from 'craft/hooks/useEditor';
import { useNodeWithModifiers } from 'craft/hooks/useNodeWithModifiers';

interface TraversalOptions {
  blacklistNodeTypes?: string[];
  whitelistNodeTypes?: string[];
  blacklistNodeIds?: string[];
}

const inOrderTraversal = (
  nodeId: string,
  helper: NodeHelpersType,
  options?: TraversalOptions,
): string[] => {
  const node = helper(nodeId).get();
  if (!node) {
    return [];
  }

  const shouldNodeBeIncluded = options
    ? isTypeMatching(node.data.name, options) && isNodeIdMatching([nodeId], options)
    : true;

  // don't return the node id if it should be blacklisted, however we still continue our traversal
  const matchingNodeId = shouldNodeBeIncluded ? [nodeId] : [];

  if (!node.data.nodes) {
    return matchingNodeId;
  }
  const nodesTraversal: string[][] = node.data.nodes.map((childId: string) =>
    inOrderTraversal(childId, helper, options),
  );
  return [...matchingNodeId, ...flatten(nodesTraversal)];
};

const isTypeMatching = (
  type: string,
  options: TraversalOptions = {
    blacklistNodeTypes: [...tooltipRootTypes, BuiltInComponents.RichTextEditor],
  },
) => {
  const { whitelistNodeTypes, blacklistNodeTypes } = options;

  if (whitelistNodeTypes && whitelistNodeTypes.indexOf(type) > -1) {
    return type;
  }
  if (blacklistNodeTypes && blacklistNodeTypes.indexOf(type) === -1) {
    return type;
  }
  return undefined;
};

/**
 * check whether a list of node contain any node defined in a blacklistNodeIds
 * @param nodeIds
 * @param options
 * @returns
 */
const isNodeIdMatching = (nodeIds: string[], options: TraversalOptions) => {
  const { blacklistNodeIds } = options;
  if (
    !blacklistNodeIds ||
    (blacklistNodeIds &&
      blacklistNodeIds.findIndex((blackListNodeId) => nodeIds.includes(blackListNodeId)) === -1)
  ) {
    return nodeIds;
  }
  return undefined;
};

// TODO: this function can probably be optimized with some memoization
export const useTraversal = () => {
  const { query } = useEditor();
  const { nodesHaveDifferentModifierState } = useNodeWithModifiers();

  const findNextIndex = (
    inOrder: string[],
    currentNodeIndex: number,
    direction: number,
    options: TraversalOptions = { blacklistNodeTypes: selectionBlacklist, blacklistNodeIds: [] },
  ): string | undefined => {
    const currentNodeId = inOrder[currentNodeIndex];
    let iterationIndex = currentNodeIndex + direction;
    while (iterationIndex >= 0 && iterationIndex < inOrder.length) {
      const nodeId = inOrder[iterationIndex];
      const { data } = query.node(nodeId).get();

      // check whether the "next" node belongs to a different modifier of the same parent (ex: formStep 1 vs formStep 0 for the source node)
      const commonModifierAncestor = nodesHaveDifferentModifierState(currentNodeId, nodeId);
      if (
        commonModifierAncestor &&
        options.blacklistNodeIds?.indexOf(commonModifierAncestor) === -1
      ) {
        return findNextIndex(inOrder, currentNodeIndex, direction, {
          ...options,
          blacklistNodeIds: [commonModifierAncestor],
        });
      }

      if (
        isTypeMatching(data?.name, options) &&
        isNodeIdMatching(query.node(nodeId).ancestors(true), options)
      ) {
        return nodeId;
      }
      iterationIndex += direction;
    }
    return undefined;
  };

  const traverseWithDirection = (
    nodeId: string,
    direction: number,
    options?: TraversalOptions,
  ): string | undefined => {
    const inOrder = inOrderTraversal(CRAFT_ROOT_ID, query.node);
    const currentNodeIndex = inOrder.indexOf(nodeId);

    if (currentNodeIndex === -1) {
      // The node you wanted does not exist
      return undefined;
    }
    return findNextIndex(inOrder, currentNodeIndex, direction, options);
  };

  return {
    ancestors: (
      nodeId: string,
      options: TraversalOptions = { blacklistNodeTypes: selectionBlacklist, blacklistNodeIds: [] },
    ) =>
      query
        .node(nodeId)
        ?.ancestors(true)
        .filter((ancestorNodeId: NodeId) => {
          const { data } = query.node(ancestorNodeId).get();
          return !!isTypeMatching(data?.name, options);
        }),

    /**
     * Gives a list of all the node ids below the current node id.
     * NOTE! It also includes the current nodeId.
     *
     * @param nodeId
     */
    descendants: (nodeId: string, options?: TraversalOptions) =>
      inOrderTraversal(nodeId, query.node, options),

    /**
     * Finds the next node by performing an in order traversal
     *
     * @param nodeId
     * @param options any additional parameter to pass into `previousNodeId`
     */
    nextNodeId: (nodeId: string, options?: TraversalOptions) =>
      traverseWithDirection(nodeId, 1, options),

    /**
     * Finds the previous node by performing an in order traversal
     *
     * @param nodeId
     * @param options any additional parameter to pass into `previousNodeId`
     */
    previousNodeId: (nodeId: string, options?: TraversalOptions) =>
      traverseWithDirection(nodeId, -1, options),
  };
};
